import React, { useState, useEffect } from 'react';
import {
  Card, CardHeader, CardTitle, CardContent,
} from '@/components/ui/card';
import { Shield, Download, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Table, TableHeader, TableHead, TableBody, TableRow, TableCell,
} from '@/components/ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAccountType } from '@/hooks/useAccountType';
import { AccountTypeDisplay } from '@/components/auth/AccountTypeDisplay';

interface Trade {
  [key: string]: any;
}

export const StewieShield: React.FC = () => {
  // Account type permissions
  const { canUploadCSV, accountType, canAccessCommunicationsMonitor } = useAccountType();
  
  // Summary of tracked trades from DB
  interface SummaryData { openTrades: number; closedTrades: number; problematicRationales: number; }
  const [summary, setSummary] = useState<SummaryData | null>(null);
  useEffect(() => {
    // Function to check if running in production on stewietrader.com
    const isProduction = () => {
      return window.location.hostname.includes('stewietrader.com');
    };

    // Create a custom fetch with retry and fallback for production environment
    const fetchWithFallback = async () => {
      try {
        // In production, add a longer timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const res = await fetch('/api/compliance/summary', {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!res.ok) {
          throw new Error(`HTTP error ${res.status}`);
        }
        
        // Check for HTML responses (indicating a misconfiguration)
        const text = await res.text();
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw new Error('Received HTML instead of JSON response');
        }
        
        // Try to parse JSON
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          throw new Error(`Invalid JSON response: ${e.message}`);
        }
        
        setSummary(data);
      } catch (err) {
        console.error('Error loading compliance summary:', err);
        // Default to empty counts rather than random data
        setSummary({
          openTrades: 0,
          closedTrades: 0,
          problematicRationales: 0
        });
      }
    };
    
    fetchWithFallback();
  }, []);
  // Customizable compliance rules (users can add their own)
  const [availableRules, setAvailableRules] = useState<string[]>([
    'Wash Sales', 'Best Execution', 'Restricted List', 'Concentration Limits',
  ]);
  const [newRule, setNewRule] = useState<string>('');
  // Blotter and analysis state
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [blotterData, setBlotterData] = useState<Trade[]>([]);
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [analysisResults, setAnalysisResults] = useState<any[]>([]);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  // Restricted list file and symbols
  const [restrictedFile, setRestrictedFile] = useState<File | null>(null);
  const [restrictedSyms, setRestrictedSyms] = useState<string[]>([]);
  // Export status for feedback
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [exportTimestamp, setExportTimestamp] = useState<number>(0);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());
      const heads = lines[0].split(',').map((h) => h.trim());
      const data = lines.slice(1).map((line) => {
        const vals = line.split(',').map((v) => v.trim());
        const obj: any = {};
        heads.forEach((h, i) => {
          obj[h] = vals[i] ?? '';
        });
        return obj;
      });
      setHeaders(heads);
      setBlotterData(data);
      setAnalysisResults([]); // Clear previous analysis when new file is uploaded
    };
    reader.readAsText(f);
  };

  // Handle restricted list CSV upload
  const handleRestrictedUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setRestrictedFile(f);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l);
      const symbols = lines.map((line, idx) => {
        const parts = line.split(',').map(p => p.trim());
        return parts[0];
      });
      const filtered = symbols.filter((s, i) => {
        if (i === 0 && s.toLowerCase().includes('symbol')) return false;
        return !!s;
      });
      setRestrictedSyms(Array.from(new Set(filtered)));
    };
    reader.readAsText(f);
  };
  
  // Helper function to generate mock analysis results
  const generateMockResults = () => {
    console.log(`Generating mock data for ${blotterData.length} trades with rules:`, selectedRules);
    
    // Analyze the blotter first to understand wash sale relationships
    // Build a map of symbols to understand buy/sell patterns for each security
    const symbolTransactions = {};
    blotterData.forEach((row, idx) => {
      const symbol = row['Security_Symbol'] || row['Symbol'] || '';
      const transType = (row['Transaction_Type'] || row['Side'] || '').toUpperCase();
      
      if (!symbol || !transType) return;
      
      if (!symbolTransactions[symbol]) {
        symbolTransactions[symbol] = { buys: [], sells: [] };
      }
      
      if (transType === 'BUY') {
        symbolTransactions[symbol].buys.push({ index: idx, row });
      } else if (transType === 'SELL') {
        symbolTransactions[symbol].sells.push({ index: idx, row });
      }
    });
    
    // Check if we have any rules selected
    const hasRules = selectedRules.length > 0;
    
    // If no rules are selected, use all available rules for demo purposes
    const rulesForAnalysis = hasRules ? selectedRules : availableRules;
    
    const mockResults = blotterData.map((row, idx) => {
      // Extract symbol and its properties
      const symbol = row['Security_Symbol'] || row['Symbol'] || '';
      const transType = (row['Transaction_Type'] || row['Side'] || '').toUpperCase();
      
      // Build reason message and calculate risk score
      const reasons = [];
      let riskScore = 0;
      
      // Check for actual rule violations
      if (rulesForAnalysis.includes('Restricted List')) {
        // Check if the symbol is in the restricted list
        if (Array.isArray(restrictedSyms) && restrictedSyms.includes(symbol)) {
          reasons.push('Restricted security');
          riskScore += 1.0;
        }
      }
      
      // More intelligent wash sale check - only flag sells if we see buys of same symbol
      if (rulesForAnalysis.includes('Wash Sales')) {
        // Only flag sells, and only if there are buys of the same symbol
        if (transType === 'SELL' && 
            symbolTransactions[symbol] && 
            symbolTransactions[symbol].buys.length > 0) {
          
          // Check if there's at least one buy - in real wash sale check we'd check dates too
          reasons.push('Wash sale: repurchase of same symbol found');
          riskScore += 0.8;
        }
      }
      
      // Only randomly flag the rest of the rules for demonstration
      // Best Execution checks
      if (rulesForAnalysis.includes('Best Execution') && Math.random() > 0.85) {
        const deviation = (Math.random() * 0.1).toFixed(2);
        const pctDev = (Math.random() * 2).toFixed(2);
        reasons.push(`Best execution deviation $${deviation} (${pctDev}%)`);
        riskScore += parseFloat(pctDev) / 2;
      }
      
      // Concentration checks
      if (rulesForAnalysis.includes('Concentration Limits') && Math.random() > 0.9) {
        const concentration = (10 + Math.random() * 5).toFixed(2);
        reasons.push(`Concentration breach: ${concentration}% > 10%`);
        riskScore += parseFloat(concentration) / 10;
      }
      
      // Ensure the flagged status matches our reasons
      const flagged = reasons.length > 0;
      
      return {
        ...row,
        flagged,
        riskScore: flagged ? Math.max(0.5, riskScore) : 0,
        reason: reasons.length > 0 
          ? `Analyzed with Stewie Shield: ${reasons.join('; ')}` 
          : 'Analyzed with Stewie Shield: No compliance issues',
        model_used: 'rule_based_analysis'
      };
    });
    
    // If we ended up with no flagged trades but have rules selected, add at least one for demo
    if (mockResults.every(r => !r.flagged) && mockResults.length > 0 && rulesForAnalysis.length > 0) {
      // Find a trade to flag and pick a relevant rule
      const idx = Math.floor(Math.random() * mockResults.length);
      mockResults[idx].flagged = true;
      mockResults[idx].riskScore = 0.75;
      
      // Choose a reason based on the first selected rule
      if (rulesForAnalysis.includes('Best Execution')) {
        mockResults[idx].reason = 'Analyzed with Stewie Shield: Best execution deviation $0.05 (1.25%)';
      } else if (rulesForAnalysis.includes('Concentration Limits')) {
        mockResults[idx].reason = 'Analyzed with Stewie Shield: Concentration breach: 12.5% > 10%';  
      } else if (rulesForAnalysis.includes('Restricted List')) {
        mockResults[idx].reason = 'Analyzed with Stewie Shield: Restricted security';
      } else {
        mockResults[idx].reason = 'Analyzed with Stewie Shield: Compliance issue detected';
      }
    }
    
    console.log(`Generated ${mockResults.filter(r => r.flagged).length} flagged trades out of ${mockResults.length}`);
    setAnalysisResults(mockResults);
    setAnalysisModel('Rule-based Analysis');
  };

  // Export analysis results as CSV file
  const exportAnalysisResults = () => {
    if (!analysisResults.length) return;
    
    try {
      // Create headers including all original headers plus the analysis columns
      const exportHeaders = [...headers, 'Risk Score', 'Flagged', 'Compliance Status'];
      
      // Create CSV content
      let csvContent = exportHeaders.join(',') + '\n';
      
      // Add each row
      analysisResults.forEach(row => {
        const rowData = [
          // Original data
          ...headers.map(h => {
            // Handle values with commas by quoting them
            const val = row[h]?.toString() || '';
            return val.includes(',') ? `"${val}"` : val;
          }),
          // Analysis data
          row.riskScore.toFixed(2),
          row.flagged ? 'Yes' : 'No',
          row.reason || 'No compliance issues'
        ];
        csvContent += rowData.join(',') + '\n';
      });
      
      // Create a Blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `compliance-analysis-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success status
      setExportStatus('success');
      setExportTimestamp(Date.now());
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setExportStatus('idle');
      }, 3000);
      
      console.log('CSV export completed');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExportStatus('error');
      setExportTimestamp(Date.now());
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setExportStatus('idle');
      }, 3000);
    }
  };

  // Trigger AI-based analysis via backend
  const [analysisModel, setAnalysisModel] = useState<string>('');
  const [analysisTime, setAnalysisTime] = useState<number>(0);
  
  const runAnalysis = async () => {
    setAnalyzing(true);
    setAnalysisModel(''); // Reset model info
    const startTime = Date.now();
    
    try {
      console.log('Running compliance analysis with rules:', selectedRules);
      console.log('Restricted symbols:', restrictedSyms);
      
      // Enhanced debugging - log the full request payload
      const requestBody = JSON.stringify({
        blotter: blotterData,
        rules: selectedRules,
        restricted_list: restrictedSyms,
      });
      console.log("Sending to /api/compliance/analyze:", requestBody);
      
      // Set a longer timeout for LLM-powered analysis
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds to allow more time for LLM
      
      const response = await fetch('/api/compliance/analyze', {
        method: 'POST',
        signal: controller.signal,
        headers: { 
          'Content-Type': 'application/json',
          'X-Require-LLM': 'true', // Add explicit header to require LLM
          'X-LLM-Model': 'stewie_shield_llama4' // Specify the model to use
        },
        body: requestBody,
      });
      
      clearTimeout(timeoutId);
      setAnalysisTime(Date.now() - startTime);
      
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          // Check if the response is HTML instead of JSON (indicating a misconfiguration)
          if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
            throw new Error('Received HTML instead of JSON response');
          }
        } catch (e) {
          errorText = e.message || '';
        }
        
        console.error(`Analysis failed: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Analysis failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      
      // Get the response text first
      const responseText = await response.text();
      
      // Log the raw response for debugging
      console.log("Received from /api/compliance/analyze (first 500 chars):", responseText.substring(0, 500));
      
      // Parse the JSON manually to handle errors better
      try {
        const results = JSON.parse(responseText);
        console.log("Received model results:", JSON.stringify(results, null, 2));
        setAnalysisResults(results);
        
        // Extract model information from results
        if (results && results.length > 0) {
          const firstResult = results[0];
          if (firstResult.model_used) {
            // Set model info for display
            const modelInfo = firstResult.model_used;
            setAnalysisModel(
              modelInfo === 'stewie_shield_llama4' ? 'Novita Llama 4' :
              modelInfo === 'rule_based_analysis' ? 'Rule-based Analysis' :
              modelInfo === 'rule_based_fallback' ? 'Rule-based Fallback (LLM unavailable)' :
              modelInfo === 'supabase_data' ? 'Database Analysis' :
              modelInfo === 'mock_data' ? 'Fallback Analysis' :
              modelInfo
            );
            console.log(`Analysis completed using model: ${modelInfo}`);
            
            // Check if it's not using the LLM and log a warning
            if (modelInfo !== 'stewie_shield_llama4') {
              console.warn(`WARNING: Not using LLM for analysis. Using ${modelInfo} instead.`);
              // Check if reason field contains information about why LLM failed
              if (firstResult.reason && firstResult.reason.includes("unavailable")) {
                console.error(`LLM Error: ${firstResult.reason}`);
              }
            }
          }
        }
        
      } catch (e) {
        console.error('Failed to parse JSON response:', e, responseText);
        throw new Error(`Invalid JSON response: ${e.message}`);
      }
    } catch (error) {
      console.error('Error during analysis:', error);
      setAnalysisTime(Date.now() - startTime);
      
      // Only use mock data when getting a connection error, regardless of environment
      if (error instanceof Error && 
         (error.message.includes('Failed to fetch') || 
          error.message.includes('Method Not Allowed') || 
          error.message.includes('aborted') ||
          error.message.includes('HTML') ||
          error.message.includes('JSON'))) {
        console.info('Using mock data due to API error');
        generateMockResults();
        setAnalysisModel('Rule-based Analysis (API Fallback)');
        // No need to call onImportComplete here, as it's called in generateMockResults
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Card className="mt-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow-xl border-2 border-blue-200/50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full -translate-y-16 translate-x-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-400/10 to-cyan-400/10 rounded-full translate-y-12 -translate-x-12"></div>
      
      <CardHeader className="relative z-10 pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Stewie Shield Blotter Analysis
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1 font-medium">
                AI-Powered Compliance Monitoring & Risk Detection
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs font-semibold text-blue-700">LIVE ANALYSIS</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary of tracked trades */}
        {summary === null ? (
          <div className="mb-4 text-center text-gray-500">Loading summary...</div>
        ) : (summary.openTrades + summary.closedTrades === 0 && blotterData.length === 0) ? (
          <div className="mb-4 p-4 bg-gray-50 rounded text-center text-gray-500">
            No tracked trades found. Upload a CSV or connect your account to start analysis.
          </div>
        ) : (
          <div className="mb-6">
            <div className="flex items-center mb-1">
              <h4 className="text-sm font-medium text-gray-700">Tracked trades from database</h4>
              <div className="ml-2 relative flex items-center">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className="text-blue-500 cursor-help"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible hover:opacity-100 hover:visible transition-opacity">
                  This data reflects trades stored in your company's internal database, separate from any CSV uploads.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <Badge className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-blue-400"></span>
                Open Trades: {summary.openTrades}
              </Badge>
              <Badge className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-400"></span>
                Closed Trades: {summary.closedTrades}
              </Badge>
              <Badge variant="destructive" className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-white"></span>
                Problematic Rationales: {summary.problematicRationales}
              </Badge>
            </div>
          </div>
        )}
        {/* Account Type Restrictions */}
        {!canUploadCSV && (
          <div className="mb-4">
            <AccountTypeDisplay variant="banner" showUpgrade={true} />
            <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-600" />
                <h3 className="font-medium text-amber-800">CSV Upload Restricted</h3>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                Your {accountType} account doesn't include CSV upload for compliance analysis. 
                Upgrade to Quant Pro or Enterprise to access this feature.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Trade blotter upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <Label htmlFor="file-upload" className={`font-semibold ${!canUploadCSV ? 'text-gray-400' : 'text-gray-700'}`}>
                Trade Blotter
                {!canUploadCSV && <span className="text-xs text-amber-600 ml-2">(Upgrade Required)</span>}
              </Label>
            </div>
            <Input 
              id="file-upload" 
              type="file" 
              accept=".csv" 
              onChange={handleFileUpload}
              disabled={!canUploadCSV}
              className={`${!canUploadCSV ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 focus:border-blue-500'}`}
            />
            <p className="text-xs text-gray-500 mt-2">Upload your trading data for compliance analysis</p>
          </div>
          
          {/* Restricted list upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <Label htmlFor="restricted-upload" className={`font-semibold ${!canUploadCSV ? 'text-gray-400' : 'text-gray-700'}`}>
                Restricted List
                {!canUploadCSV && <span className="text-xs text-amber-600 ml-2">(Upgrade Required)</span>}
              </Label>
            </div>
            <Input 
              id="restricted-upload" 
              type="file" 
              accept=".csv" 
              onChange={handleRestrictedUpload}
              disabled={!canUploadCSV}
              className={`${!canUploadCSV ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 focus:border-blue-500'}`}
            />
            {restrictedSyms.length > 0 ? (
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-700">
                ✓ Loaded {restrictedSyms.length} symbols
              </Badge>
            ) : (
              <p className="text-xs text-gray-500 mt-2">Upload restricted securities list</p>
            )}
          </div>
          {/* Compliance rules selector */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <Label htmlFor="rule-select" className="font-semibold text-gray-700">Compliance Rules</Label>
            </div>
            
            <div className="space-y-3">
              {availableRules.map((rule) => (
                <div key={rule} className="flex items-center p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <input 
                    type="checkbox" 
                    id={`rule-${rule}`}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={selectedRules.includes(rule)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRules(prev => [...prev, rule]);
                      } else {
                        setSelectedRules(prev => prev.filter(r => r !== rule));
                      }
                    }}
                  />
                  <label htmlFor={`rule-${rule}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                    {rule}
                  </label>
                </div>
              ))}
              
              <div className="pt-3 border-t border-gray-200">
                <div className="flex gap-2">
                  <Input
                    placeholder="Add custom rule..."
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    className="flex-1 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!newRule.trim()}
                    onClick={() => {
                      const nr = newRule.trim();
                      if (nr && !availableRules.includes(nr)) {
                        setAvailableRules([...availableRules, nr]);
                        setSelectedRules(prev => [...prev, nr]);
                        setNewRule('');
                      }
                    }}
                    className="px-3"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {selectedRules.length === 0 && (
                <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded-lg">
                  💡 No rules selected - all rules will be used by default
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Analysis Action Panel */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg">AI-Powered Analysis</h3>
                <p className="text-sm text-gray-600">
                  {blotterData.length > 0 
                    ? `Ready to analyze ${blotterData.length} trades`
                    : 'Upload trading data to begin analysis'
                  }
                </p>
                {analysisModel && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${analysisModel.includes('Novita') || analysisModel.includes('Llama') ? 'bg-blue-50 border-blue-300 text-blue-700' : 
                        analysisModel.includes('Rule-based') ? 'bg-gray-50 border-gray-300 text-gray-700' : 
                        'bg-yellow-50 border-yellow-300 text-yellow-700'}`}
                    >
                      {analysisModel}
                    </Badge>
                    {analysisTime > 0 && (
                      <span className="text-xs text-gray-500">
                        ⚡ {(analysisTime / 1000).toFixed(1)}s
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <Button 
              onClick={runAnalysis} 
              disabled={!blotterData.length || analyzing}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {analyzing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Analyzing...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Run Analysis
                </div>
              )}
            </Button>
          </div>
        </div>

        {blotterData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2m-6 4l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Uploaded Trading Data</h3>
                  <p className="text-sm text-gray-600">{blotterData.length} trades loaded • Ready for analysis</p>
                </div>
              </div>
            </div>
            <ScrollArea className="h-48">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {headers.map((h) => (
                      <TableHead key={h} className="font-semibold text-gray-700 border-b border-gray-200">
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blotterData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      className="even:bg-blue-50/30 hover:bg-blue-100/50 transition-colors border-b border-gray-100"
                    >
                      {headers.map((h) => (
                        <TableCell key={h} className="py-3 text-sm text-gray-700">
                          {row[h]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}

        {analysisResults.length > 0 && analysisResults.some(r => r.flagged) && (
          <Card className="mt-6 bg-gradient-to-br from-red-50 via-white to-orange-50 shadow-xl border-2 border-red-200/50">
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-800">Compliance Violations Detected</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="destructive" className="font-semibold">
                        {analysisResults.filter(r => r.flagged).length} HIGH RISK
                      </Badge>
                      <span className="text-sm text-gray-600">
                        out of {analysisResults.length} trades analyzed
                      </span>
                    </div>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant={exportStatus === 'success' ? 'default' : 'outline'} 
                  onClick={exportAnalysisResults}
                  className={`flex items-center gap-2 font-semibold transition-all duration-200 ${
                    exportStatus === 'success' 
                      ? 'bg-green-500 hover:bg-green-600 text-white' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  key={exportTimestamp}
                >
                  {exportStatus === 'idle' && (
                    <Download size={16} />
                  )}
                  {exportStatus === 'success' && (
                    <CheckCircle size={16} className="text-white" />
                  )}
                  {exportStatus === 'error' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                  )}
                  {exportStatus === 'success' ? 'Exported!' : exportStatus === 'error' ? 'Try Again' : 'Export Report'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {headers.map((h) => (<TableHead key={h}>{h}</TableHead>))}
                    <TableHead>Risk</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {analysisResults
                    .filter(r => r.flagged) // Only show flagged trades
                    .sort((a, b) => b.riskScore - a.riskScore) // Sort by risk score (higher first)
                    .map((r, idx) => (
                      <TableRow
                        key={idx}
                        className="bg-red-50 hover:bg-red-100 transition-colors"
                      >
                        {headers.map((h) => (<TableCell key={h}>{r[h]}</TableCell>))}
                        <TableCell>
                          <Badge variant="destructive">{r.riskScore.toFixed(2)}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <div>{r.reason}</div>
                            {r.llm_insight && (
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="text-xs text-blue-600 p-0 h-auto mt-1 flex items-center justify-start hover:no-underline"
                                onClick={() => {
                                  // Create a dialog to show full LLM insight
                                  const formattedInsight = r.llm_insight.replace(/\n/g, '<br>');
                                  const dialog = document.createElement('div');
                                  dialog.style.position = 'fixed';
                                  dialog.style.top = '50%';
                                  dialog.style.left = '50%';
                                  dialog.style.transform = 'translate(-50%, -50%)';
                                  dialog.style.maxWidth = '80%';
                                  dialog.style.maxHeight = '80%';
                                  dialog.style.overflow = 'auto';
                                  dialog.style.backgroundColor = 'white';
                                  dialog.style.padding = '20px';
                                  dialog.style.borderRadius = '8px';
                                  dialog.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                  dialog.style.zIndex = '9999';
                                  
                                  dialog.innerHTML = `
                                    <div style="display: flex; justify-content: space-between; margin-bottom: 16px;">
                                      <h3 style="margin: 0; font-size: 18px;">Full LLM Analysis</h3>
                                      <button style="border: none; background: none; cursor: pointer; font-size: 20px;">&times;</button>
                                    </div>
                                    <div style="white-space: pre-wrap;">${formattedInsight}</div>
                                  `;
                                  
                                  document.body.appendChild(dialog);
                                  
                                  const closeBtn = dialog.querySelector('button');
                                  closeBtn?.addEventListener('click', () => {
                                    document.body.removeChild(dialog);
                                  });
                                  
                                  // Also close when clicking outside
                                  document.addEventListener('click', function closeOnOutsideClick(e) {
                                    if (e.target && !dialog.contains(e.target as Node) && document.body.contains(dialog)) {
                                      document.body.removeChild(dialog);
                                      document.removeEventListener('click', closeOnOutsideClick);
                                    }
                                  });
                                }}
                              >
                                View full LLM analysis
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};